import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../Services/api'

/* A cursor-paged story feed (spec 9.3). Each feed's state is remembered per
   key for a while, so going Back shows the same stories and the browser can
   restore the scroll position (spec 6). refresh() reloads the first page and
   marks stories that weren't there before as new (NOVO). */
const store = new Map()
const MAX_FEEDS = 30
const STALE_MS = 10 * 60 * 1000

const EMPTY = {
  status: 'loading', // loading | ready | error
  items: [],
  mainStory: null,
  nextCursor: null,
  asOf: null,
  snapshot: null,
  extra: {},
  newIds: [],
  loadingMore: false,
  moreError: null,
  refreshing: false,
  loadedAt: 0,
  error: null,
}

function idsOf(state) {
  return new Set([state.mainStory?.id, ...state.items.map((i) => i.id)].filter(Boolean))
}

function remember(key, value) {
  store.delete(key)
  store.set(key, value)
  while (store.size > MAX_FEEDS) store.delete(store.keys().next().value)
}

export function useFeed(key, fetchPage, { enabled = true } = {}) {
  const [local, setLocal] = useState(() => ({ key, value: store.get(key) || EMPTY }))
  // Only this key's state: right after the key changes (one topic to
  // another) the render before the effect below must not show the previous
  // feed, or a page takes its topic for a redirect and goes back there.
  const state = local.key === key ? local.value : store.get(key) || EMPTY
  const keyRef = useRef(key)
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage

  const set = useCallback((k, updater) => {
    const prev = store.get(k) || EMPTY
    const next = typeof updater === 'function' ? updater(prev) : updater
    remember(k, next)
    if (keyRef.current === k) setLocal({ key: k, value: next })
  }, [])

  const loadFirst = useCallback(
    async (k, { markNew = false, keepOld = false } = {}) => {
      const before = store.get(k)
      set(k, keepOld && before ? { ...before, refreshing: true, error: null } : EMPTY)
      try {
        const body = await fetchRef.current({ cursor: null, refresh: keepOld })
        const {
          items = [],
          mainStory = null,
          nextCursor = null,
          asOf = null,
          snapshot = null,
          ...extra
        } = body
        const old = markNew && before?.status === 'ready' ? idsOf(before) : null
        const newIds = old
          ? [mainStory, ...items].filter((i) => i && !old.has(i.id)).map((i) => i.id)
          : []
        set(k, {
          ...EMPTY,
          status: 'ready',
          items,
          mainStory,
          nextCursor,
          asOf,
          snapshot,
          extra,
          newIds,
          loadedAt: Date.now(),
        })
        return { newCount: newIds.length }
      } catch (error) {
        if (keepOld && before?.status === 'ready') {
          set(k, { ...before, refreshing: false })
          return { newCount: 0, error }
        }
        set(k, { ...EMPTY, status: 'error', error })
        return { newCount: 0, error }
      }
    },
    [set]
  )

  useEffect(() => {
    keyRef.current = key
    if (!enabled) return
    const cached = store.get(key)
    if (
      cached &&
      (cached.status === 'ready' || cached.refreshing) &&
      Date.now() - cached.loadedAt < STALE_MS
    ) {
      setLocal({ key, value: cached })
      return
    }
    setLocal({ key, value: EMPTY })
    loadFirst(key)
  }, [key, enabled, loadFirst])

  const loadMore = useCallback(async () => {
    const k = keyRef.current
    const s = store.get(k)
    if (!s || s.status !== 'ready' || !s.nextCursor || s.loadingMore || s.refreshing) return
    const cursor = s.nextCursor
    set(k, (x) => ({ ...x, loadingMore: true, moreError: null }))
    try {
      const body = await fetchRef.current({ cursor })
      set(k, (x) => {
        if (x.nextCursor !== cursor) return { ...x, loadingMore: false } // refreshed meanwhile
        const seen = idsOf(x)
        const more = (body.items || []).filter((i) => !seen.has(i.id))
        return {
          ...x,
          items: [...x.items, ...more],
          nextCursor: body.nextCursor || null,
          loadingMore: false,
        }
      })
    } catch (error) {
      set(k, (x) => ({ ...x, loadingMore: false, moreError: error }))
    }
  }, [set])

  const refresh = useCallback(
    (options = {}) =>
      loadFirst(keyRef.current, { markNew: options.markNew ?? true, keepOld: true }),
    [loadFirst]
  )
  const retry = useCallback(() => loadFirst(keyRef.current), [loadFirst])

  const newIds = useMemo(() => new Set(state.newIds), [state.newIds])
  return { ...state, newIds, loadMore, refresh, retry }
}

const POLL_MS = 90 * 1000
const QUIET_AFTER_REFRESH_MS = 2 * 60 * 1000
const AWAY_REFRESH_MS = 12 * 60 * 1000

/* The "↑ N novih vijesti" pill (spec 5.4, 9.5): polls while the tab is
   visible, stays hidden for 2 min after a refresh, and calls onAway() when
   the reader comes back after more than ~12 min (the feed then refreshes
   quietly instead). */
export function useNewStories({ enabled, category, sort, asOf, snapshot, loadedAt, onAway }) {
  const [result, setResult] = useState({ count: 0, sources: [], asOf: null })
  const [, setTick] = useState(0)
  const onAwayRef = useRef(onAway)
  onAwayRef.current = onAway

  useEffect(() => {
    if (!enabled || !asOf) return undefined
    let alive = true
    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : null
    const check = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const r = await api.newCount({ category, sort, since: asOf, snapshot })
        if (alive) setResult({ count: r.count || 0, sources: r.sources || [], asOf })
      } catch (e) {
        // keep the last answer
      }
      if (alive) setTick((n) => n + 1)
    }
    const timer = setInterval(check, POLL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      const away = hiddenAt ? Date.now() - hiddenAt : 0
      hiddenAt = null
      if (away > AWAY_REFRESH_MS) onAwayRef.current?.()
      else check()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      alive = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, category, sort, asOf, snapshot])

  const current = result.asOf === asOf && enabled
  const quiet = Date.now() - loadedAt < QUIET_AFTER_REFRESH_MS
  return current && !quiet
    ? { count: result.count, sources: result.sources }
    : { count: 0, sources: [] }
}
